"use client"

import { createClient, Session, SupabaseClient } from '@supabase/supabase-js'
import { SUPABASE_PUBLIC_URL, SUPABASE_PUBLIC_ANON_KEY } from '@/lib/supabase-config'

interface AuthManagerConfig {
  refreshThreshold?: number // How many seconds before expiry to refresh (default: 5 minutes)
  checkInterval?: number // How often to check for expiry (default: 30 seconds)
}

// Shared Supabase client instance for auth-manager (avoids multiple GoTrueClient instances)
let sharedAuthClient: SupabaseClient | null = null

function getAuthClient(): SupabaseClient {
  if (!sharedAuthClient) {
    // Inert on the mycio.ai host: MyCIO owns its own session (getMyCIOClient) against the SAME Supabase
    // project, so a second auto-refreshing client here rotates the same refresh token -> token_revoked /
    // 429 infinite loop that froze the MyCIO pages. Don't persist or auto-refresh on mycio.ai.
    const onMyCio =
      typeof window !== 'undefined' &&
      (/(?:^|\.)mycio\.ai$/i.test(window.location.hostname) || window.location.pathname.startsWith('/'))
    sharedAuthClient = createClient(
      SUPABASE_PUBLIC_URL,
      SUPABASE_PUBLIC_ANON_KEY,
      {
        auth: {
          persistSession: !onMyCio,
          autoRefreshToken: !onMyCio,
        },
      }
    )
  }
  return sharedAuthClient
}

class AuthManager {
  private static instance: AuthManager
  private refreshTimer: NodeJS.Timeout | null = null
  private lastActivity: number = Date.now()
  private refreshThreshold: number = 5 * 60 // 5 minutes before expiry
  private checkInterval: number = 30 * 1000 // Check every 30 seconds
  private isRefreshing: boolean = false
  private refreshPromise: Promise<boolean> | null = null
  private supabase: SupabaseClient

  private constructor(config?: AuthManagerConfig) {
    if (config?.refreshThreshold) {
      this.refreshThreshold = config.refreshThreshold
    }
    if (config?.checkInterval) {
      this.checkInterval = config.checkInterval
    }

    // Use module-level shared client
    this.supabase = getAuthClient()

    // Start monitoring
    this.startTokenMonitoring()

    // Track user activity
    if (typeof window !== 'undefined') {
      window.addEventListener('mousemove', this.updateActivity)
      window.addEventListener('keypress', this.updateActivity)
      window.addEventListener('click', this.updateActivity)
      window.addEventListener('scroll', this.updateActivity)
    }
  }

  static getInstance(config?: AuthManagerConfig): AuthManager {
    if (!AuthManager.instance) {
      AuthManager.instance = new AuthManager(config)
    }
    return AuthManager.instance
  }

  private updateActivity = () => {
    this.lastActivity = Date.now()
  }

  private isUserActive(): boolean {
    // Consider user active if they've interacted in the last 15 minutes
    return Date.now() - this.lastActivity < 15 * 60 * 1000
  }

  private async checkAndRefreshToken() {
    try {
      const { data: { session } } = await this.supabase.auth.getSession()

      if (!session) {
        return
      }

      // Use expires_at from session object (no JWT parsing needed)
      const expiresAt = session.expires_at
      if (!expiresAt) {
        return
      }

      const now = Math.floor(Date.now() / 1000)
      const timeUntilExpiry = expiresAt - now

      // If token expires in less than threshold and user is active, refresh it
      if (timeUntilExpiry < this.refreshThreshold && this.isUserActive()) {
        console.log('Proactively refreshing access token before expiry...')
        await this.refreshSession()
      }
    } catch (error) {
      console.error('Error checking token expiry:', error)
    }
  }

  private async refreshSession(): Promise<boolean> {
    // If already refreshing, wait for that to complete
    if (this.isRefreshing && this.refreshPromise) {
      return this.refreshPromise
    }

    this.isRefreshing = true
    this.refreshPromise = this.doRefresh()

    const result = await this.refreshPromise
    this.isRefreshing = false
    this.refreshPromise = null

    return result
  }

  private async doRefresh(): Promise<boolean> {
    try {
      console.log('Refreshing access token...')
      const { data, error } = await this.supabase.auth.refreshSession()

      if (error) {
        console.error('Failed to refresh access token:', error)
        return false
      }

      if (data?.session) {
        console.log('Access token refreshed successfully')
        // Clear any cached sessions to force re-read
        if (typeof window !== 'undefined') {
          sessionStorage.removeItem('supabase-session-cache')
        }
        return true
      }

      return false
    } catch (error) {
      console.error('Error refreshing access token:', error)
      return false
    }
  }

  private startTokenMonitoring() {
    // Clear existing timer
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer)
    }

    // Check token expiry periodically
    this.refreshTimer = setInterval(() => {
      this.checkAndRefreshToken()
    }, this.checkInterval)

    // Also check immediately
    this.checkAndRefreshToken()
  }

  // Public method to manually trigger refresh on user interaction
  async refreshOnInteraction(): Promise<boolean> {
    if (!this.isUserActive()) {
      return false
    }

    try {
      const { data: { session } } = await this.supabase.auth.getSession()

      if (!session) {
        return false
      }

      // Use expires_at from session object
      const expiresAt = session.expires_at
      if (!expiresAt) {
        return false
      }

      const now = Math.floor(Date.now() / 1000)
      const timeUntilExpiry = expiresAt - now

      // Refresh if less than 10 minutes remaining
      if (timeUntilExpiry < 10 * 60) {
        return await this.refreshSession()
      }

      return true
    } catch (error) {
      console.error('Error in refreshOnInteraction:', error)
      return false
    }
  }

  // Get time until token expiry in seconds
  async getTimeUntilExpiry(): Promise<number | null> {
    try {
      const { data: { session } } = await this.supabase.auth.getSession()

      if (!session || !session.expires_at) {
        return null
      }

      // Use expires_at from session object
      const now = Math.floor(Date.now() / 1000)
      return session.expires_at - now
    } catch (error) {
      console.error('Error getting token expiry:', error)
      return null
    }
  }

  // Public method to get current session
  async getSession(): Promise<Session | null> {
    try {
      const { data: { session } } = await this.supabase.auth.getSession()
      return session
    } catch (error) {
      console.error('Error getting session:', error)
      return null
    }
  }

  // Public method to refresh access token
  async refreshAccessToken(): Promise<boolean> {
    return await this.refreshSession()
  }

  // Public method to clear session
  async clearSession(): Promise<void> {
    try {
      await this.supabase.auth.signOut()

      // Clear cached sessions
      if (typeof window !== 'undefined') {
        sessionStorage.removeItem('supabase-session-cache')
        localStorage.removeItem('supabase.auth.token')
      }
    } catch (error) {
      console.error('Error clearing session:', error)
    }
  }

  // Clean up resources
  destroy() {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer)
      this.refreshTimer = null
    }

    if (typeof window !== 'undefined') {
      window.removeEventListener('mousemove', this.updateActivity)
      window.removeEventListener('keypress', this.updateActivity)
      window.removeEventListener('click', this.updateActivity)
      window.removeEventListener('scroll', this.updateActivity)
    }
  }
}

export default AuthManager
